import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 font-display">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-3xl mb-6">
              <span
                className="material-symbols-outlined text-red-500 text-4xl"
                style={{ fontVariationSettings: '"FILL" 1' }}
              >
                error
              </span>
            </div>

            <p className="text-8xl font-black text-red-500 tracking-tighter mb-2">500</p>

            <h1 className="text-2xl font-bold text-slate-900 mb-3">Something went wrong</h1>

            <p className="text-slate-500 max-w-sm mx-auto mb-8">
              An unexpected error occurred. Try refreshing the page or go back to home.
            </p>

            {import.meta.env.DEV && this.state.errorMessage && (
              <pre className="text-left bg-red-50 border border-red-200 rounded-xl p-4 text-xs text-red-700 max-w-md mx-auto mb-6 overflow-auto">
                {this.state.errorMessage}
              </pre>
            )}

            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 bg-red-500 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-red-600 transition-colors"
              >
                <span className="material-symbols-outlined text-[18px]">refresh</span>
                Refresh page
              </button>
              <button
                onClick={() => { window.location.href = '/'; }}
                className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 font-semibold px-5 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
              >
                <span className="material-symbols-outlined text-[18px]">home</span>
                Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
